import {type FocusEvent, forwardRef, useEffect, useState} from 'react';
import ReactSelect, {type SelectInstance} from 'react-select';
import CreatableSelect from 'react-select/creatable';

import {useTheme} from 'styled-components';

import {i18n} from '@yourcoach/i18n';

import type {ThemeAndAnyColor} from '../../../styles';
import type {
  GetComponentProps,
  Nullable,
  WithDomStyleProp,
  WithTestID,
} from '../../../utils/utility-types';
import type {Icon} from '../../Icon';
import type {SelectOption} from '../types';

import {type PayloadAction, payloadActionCreator} from '../../../utils/events';
import {useForwardedRef} from '../../../utils/hooks/useForwardedRef';
import {ErrorText} from '../../TextInput/styles';
import {SelectClearIndicatorV2} from './components/SelectClearIndicator.v2';
import {SelectControlV2} from './components/SelectControl.v2';
import {SelectDropdownIndicatorV2} from './components/SelectDropdownIndicator.v2';
import {SelectGroupHeadingV2} from './components/SelectGroupHeading.v2';
import {SelectMenuV2} from './components/SelectMenu.v2';
import {SelectMultiValueV2} from './components/SelectMultiValue.v2';
import {SelectOptionV2} from './components/SelectOption.v2';
import {SelectSingleValueV2} from './components/SelectSingleValue.v2';
import {ForwardThemeWrapper} from './components/SelectThemeForwarding.v2';
import {useCloseBehaviour} from './hooks/useCloseBehaviour';
import * as S from './Select.v2.styles';
import {baseStyles} from './Select.v2.styles';

export const SelectV2 = forwardRef<
  SelectInstance,
  WithDomStyleProp<SelectV2.Props>
>(
  (
    {
      blurInputOnSelect,
      closeMenuOnSelect,
      error,
      isMulti,
      isValidNewOption,
      onChange,
      scheme = 'primary',
      size = 'me',
      testID,
      useCreatedOptionAsSearch,
      usePortal,
      ...props
    },
    ref,
  ) => {
    const appTheme = useTheme();
    const localRef = useForwardedRef(ref);

    const SelectComponent = props.isCreatable ? CreatableSelect : ReactSelect;
    const [inputValue, setInputValue] = useState('');
    const [portalisedProps, setPortalisedProps] = useState<
      Partial<GetComponentProps<typeof SelectComponent>>
    >({});

    const {blurOnSelect, closeOnSelect} = useCloseBehaviour({
      blurInputOnSelect,
      closeMenuOnSelect,
      isMulti,
    });

    const formatCreateLabel = (value: string) => {
      return props.formatCreateLabel
        ? props.formatCreateLabel(value)
        : i18n.t('shared:use_value', {value});
    };

    useEffect(() => {
      setPortalisedProps(
        usePortal
          ? {
              menuPortalTarget:
                typeof document !== 'undefined' ? document.body : null,
              menuPosition: 'fixed',
              menuShouldScrollIntoView: false,
            }
          : {},
      );
    }, [usePortal]);

    const createdOptionAsSearchProps: Partial<
      GetComponentProps<typeof SelectComponent>
    > =
      props.isCreatable && useCreatedOptionAsSearch
        ? {
            inputValue: inputValue,
            onBlur: e => {
              props.onBlur?.(e);

              setInputValue('');
            },
            onFocus: e => {
              props.onFocus?.(e);

              const selectedOption: SelectOption | undefined = localRef.current
                ?.state.selectValue[0] as SelectOption;

              setInputValue(selectedOption?.label ?? '');
            },
            onInputChange: value => setInputValue(value),
          }
        : {};

    return (
      <S.Container
        className={props.className}
        error={error}
        scheme={scheme}
        size={size}
        style={props.style}
      >
        <SelectComponent
          className="react-select-container"
          classNamePrefix="react-select"
          closeMenuOnSelect={closeOnSelect}
          components={{
            ClearIndicator: clearIndicatorProps => {
              // @ts-expect-error: who knows why???
              const extendedProps: SelectClearIndicatorV2.Props = {
                ...clearIndicatorProps,
                size,
              };

              return <SelectClearIndicatorV2 {...extendedProps} />;
            },
            Control: controlProps => {
              const extendedProps: SelectControlV2.Props = {
                ...controlProps,
                size,
              };

              return (
                <ForwardThemeWrapper
                  Component={SelectControlV2}
                  forwardTheme={controlProps.theme}
                  {...extendedProps}
                />
              );
            },
            DropdownIndicator: dropdownIndicatorProps => {
              // @ts-expect-error: who knows why???
              const extendedProps: SelectDropdownIndicatorV2.Props = {
                ...dropdownIndicatorProps,
                size,
              };

              return <SelectDropdownIndicatorV2 {...extendedProps} />;
            },
            GroupHeading: groupHeadingProps => (
              <SelectGroupHeadingV2 {...groupHeadingProps} />
            ),
            IndicatorSeparator: () => null,
            Menu: menuProps => {
              return (
                <ForwardThemeWrapper
                  Component={SelectMenuV2}
                  forwardTheme={menuProps.theme}
                  {...menuProps}
                />
              );
            },
            MultiValue: multiValueProps => (
              <SelectMultiValueV2 {...multiValueProps} />
            ),
            Option: optionProps => {
              const extendedProps: SelectOptionV2.Props = {
                ...optionProps,
                scheme,
                size,
              };

              return (
                <ForwardThemeWrapper
                  Component={SelectOptionV2}
                  forwardTheme={optionProps.theme}
                  {...extendedProps}
                />
              );
            },
            SingleValue: singleValueProps => {
              return (
                <ForwardThemeWrapper
                  Component={SelectSingleValueV2}
                  forwardTheme={singleValueProps.theme}
                  {...singleValueProps}
                />
              );
            },
          }}
          controlShouldRenderValue={true}
          hideSelectedOptions={false}
          isMulti={isMulti}
          ref={localRef}
          styles={{
            ...baseStyles,
            placeholder: base => ({
              ...base,
              color: props.placeholderColor ?? base.color,
            }),
          }}
          {...portalisedProps}
          blurInputOnSelect={blurOnSelect}
          theme={theme => ({
            ...theme,
            ...appTheme,
            colors: {...theme.colors, ...appTheme.color.v3},
          })}
          {...props}
          {...createdOptionAsSearchProps}
          data-test-id={testID}
          formatCreateLabel={formatCreateLabel}
          isValidNewOption={isValidNewOption}
          onChange={(e: SelectOption) => onChange?.(payloadActionCreator(e))}
        />
        {error ? <ErrorText>{error}</ErrorText> : null}
      </S.Container>
    );
  },
);

export namespace SelectV2 {
  export type ColorScheme = 'primary' | 'secondary';

  export type GroupSelectOption<V = SelectValue, T = object> = {
    // means the new option was created if the Select is creatable
    __isNew__?: boolean;
    label: string;
    options: SingleSelectOption<V, T>[];
  };

  export type MultiSelectChangeEventAction<
    V = SelectValue,
    T = object,
  > = PayloadAction<SingleSelectOption<V, T>[]>;

  export type Props = WithTestID & {
    /** inline styles override object*/
    blurInputOnSelect?: boolean;
    /**
     * Determines whether the menu should automatically close after a selection is made.
     * If set to true, the menu will close immediately upon selecting an option.
     * If false, the menu will remain open after a selection.
     * Optional property.
     */
    closeMenuOnSelect?: boolean;
    /**
     * Default option.
     */
    defaultValue?: SelectOption | SelectOption[];
    /**
     * Error message or just a flag to show error state
     */
    error?: boolean | string;
    /**
     * Gets the label for the "create new ..." option in the menu
     */
    formatCreateLabel?: (value: string) => string;
    id?: string;
    /**
     * Show clear cross button in the select.
     */
    isClearable?: boolean;
    /**
     * Use creatable select
     */
    isCreatable?: boolean;
    /**
     * Block any interactions with select.
     */
    isDisabled?: boolean;
    /**
     * Async options loading flag.
     */
    isLoading?: boolean;
    /**
     * Support multiple values
     */
    isMulti?: boolean;
    /**
     * Filter options by typing in input field.
     */
    isSearchable?: boolean;
    /**
     * Determines whether the "create new ..." option should be displayed
     * based on the current input value,
     * select value and options array.
     */
    isValidNewOption?: (
      inputValue: string,
      value: SelectOption[],
      options: SelectOption[],
    ) => boolean;
    /**
     * Blur event handler.
     */
    onBlur?: (event: FocusEvent) => any;
    /**
     * Change state handler.
     */
    onChange?: (action: SelectChangeEventAction) => any;
    /**
     * Focus event handler.
     */
    onFocus?: (event: FocusEvent) => any;
    /**
     * Options to display.
     */
    options: SelectOption[];
    /**
     * Text to display when there is no value selected.
     * (no initial value)
     */
    placeholder?: string;
    placeholderColor?: ThemeAndAnyColor;
    /**
     * Color scheme
     */
    scheme?: ColorScheme;
    /**
     * Height of the component
     * m - 48px; s - 40px;
     */
    size?: Size;
    /**
     * Use a label of selected option as default value in search input if the option __isNew__
     */
    useCreatedOptionAsSearch?: boolean;
    /**
     * Use this prop if the Select renders inside a modal
     * @see https://react-select.com/advanced#portaling
     */
    usePortal?: boolean;
    /**
     * External controller value
     */
    value?: Nullable<SelectOption | SelectOption[]>;
  };

  export type SelectChangeEventAction =
    | MultiSelectChangeEventAction
    | SingleSelectChangeEventAction;

  export type SelectOption<V = SelectValue, T = object> =
    | GroupSelectOption<V, T>
    | SingleSelectOption<V, T>;

  export type SingleSelectChangeEventAction<
    V = SelectValue,
    T = object,
  > = PayloadAction<SingleSelectOption<V, T>>;

  export type SingleSelectOption<V = SelectValue, T = object> = {
    // means the new option was created if the Select is creatable
    __isNew__?: boolean;
    icon?: GetComponentProps<typeof Icon>;
    label: string;
    value: V;
  } & T;

  export type Size = 'la' | 'me' | 'sm' | 'xs';

  export type StyledProps = Required<Pick<Props, 'scheme' | 'size'>> &
    Pick<Props, 'error'>;
  type SelectValue = number | string;
}
